import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { initUet } from './lib/uet';
import { BRAND, IS_PRODUCT } from './brand';
import { NuggetState } from './brand/Nugget';

// Home is eagerly loaded (landing page — no spinner needed)
import Home from './pages/Home';

// Lazy load other pages
// Dev-only email editor lab (tree-shaken out of production builds).
const EditorLab = import.meta.env.DEV ? lazyWithRetry(() => import('./pages/EditorLab')) : null;
const About = lazyWithRetry(() => import('./pages/About'));
const Services = lazyWithRetry(() => import('./pages/Services'));
const Quote = lazyWithRetry(() => import('./pages/Quote'));

const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const ChromeExtensionPrivacy = lazyWithRetry(() => import('./pages/ChromeExtensionPrivacy'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const Insights = lazyWithRetry(() => import('./pages/Insights'));
const InsightPost = lazyWithRetry(() => import('./pages/InsightPost'));
const LezFindrShowcase = lazyWithRetry(() => import('./pages/LezFindrShowcase'));
const AdminAuth = lazyWithRetry(() => import('./components/AdminAuth'));
const AdminHub = lazyWithRetry(() => import('./components/AdminHub'));
const AdminDashboard = lazyWithRetry(() => import('./components/AdminDashboard'));
const AdminProjects = lazyWithRetry(() => import('./components/AdminProjects'));
const AdminContacts = lazyWithRetry(() => import('./components/AdminContacts'));
const BlogManagement = lazyWithRetry(() => import('./components/BlogManagement'));
const FreeWebsiteForm = lazyWithRetry(() => import('./components/FreeWebsiteForm'));
const Success = lazyWithRetry(() => import('./components/Success'));
const Cancel = lazyWithRetry(() => import('./components/Cancel'));
const Thanks = lazyWithRetry(() => import('./components/Thanks'));
const GoogleOAuthCallback = lazyWithRetry(() => import('./components/GoogleOAuthCallback'));
const AuthenticatedQuoteForm = lazyWithRetry(() => import('./components/AuthenticatedQuoteForm'));
const Booking = lazyWithRetry(() => import('./pages/Booking'));
const BookingManage = lazyWithRetry(() => import('./pages/BookingManage'));
const SimpleTechHelp = lazyWithRetry(() => import('./pages/SimpleTechHelp'));
const EmailApiDocs = lazyWithRetry(() => import('./pages/EmailApiDocs'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactPage'));
const Specials = lazyWithRetry(() => import('./pages/Specials'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const Start = lazyWithRetry(() => import('./pages/Start'));
const Welcome = lazyWithRetry(() => import('./pages/Welcome'));
const AuthPage = lazyWithRetry(() => import('./pages/AuthPage'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const PartnerDashboard = lazyWithRetry(() => import('./pages/PartnerDashboard'));
const Intake = lazyWithRetry(() => import('./pages/Intake'));
const Blueprint = lazyWithRetry(() => import('./pages/Blueprint'));
const GithubCallback = lazyWithRetry(() => import('./pages/GithubCallback'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const BrainstormSession = lazyWithRetry(() => import('./pages/BrainstormSession'));

// Service pages
const CustomWebDevelopment = lazyWithRetry(() => import('./pages/services/CustomWebDevelopment'));
const MobileAppDevelopment = lazyWithRetry(() => import('./pages/services/MobileAppDevelopment'));
const AIIntegration = lazyWithRetry(() => import('./pages/services/AIIntegration'));
const EmailMarketingSaaS = lazyWithRetry(() => import('./pages/services/EmailMarketingSaaS'));
const EmailMarketingLP = lazyWithRetry(() => import('./pages/lp/EmailMarketingLP'));
const WebsiteLP = lazyWithRetry(() => import('./pages/lp/WebsiteLP'));
const TechnologyConsulting = lazyWithRetry(() => import('./pages/services/TechnologyConsulting'));

// Portfolio pages
const PortfolioPage = lazyWithRetry(() => import('./pages/PortfolioPage'));
const MoffattRanch = lazyWithRetry(() => import('./pages/portfolio/MoffattRanch'));
const RavenscroftDesign = lazyWithRetry(() => import('./pages/portfolio/RavenscroftDesign'));
const NeeshArt = lazyWithRetry(() => import('./pages/portfolio/NeeshArt'));
const Mirada = lazyWithRetry(() => import('./pages/portfolio/Mirada'));

// Small components can stay as regular imports
import ScrollToTop from './components/ScrollToTop';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';

// FloatingChat is heavy (imports framer-motion + supabase) — lazy-load it
// and only render on non-admin routes so it doesn't load on dashboard, admin, etc.
const FloatingChat = lazyWithRetry(() => import('./components/FloatingChat'));

// Route-aware wrapper: only renders FloatingChat on non-admin routes
// This prevents the lazy chunk from ever being requested on admin pages
const FloatingChatRoute = () => {
  const location = useLocation();
  // /welcome is the calm referral landing — no chat widget competing for
  // attention there (its form has an opt-in assist instead).
  const isAdmin = ['/admin', '/admin-hub', '/admin-panel', '/admin-projects', '/admin-contacts', '/admin-blog', '/dashboard', '/client-portal', '/welcome'].some(route =>
    location.pathname.startsWith(route)
  );
  if (isAdmin) return null;
  return <FloatingChat />;
};

import InlineLoadingAnimation from './components/InlineLoadingAnimation';
import { ToastProvider } from './components/primitives';
import { AnimatePresence } from 'framer-motion';
import { trackPage } from './lib/productEvents';

// Performance optimization: dynamically import heavy admin components (BlogManagement already imported above)

// Product mode (design system "loading" state): Nugget wags while the page
// loads. Nugget swaps to the still sitting pose under prefers-reduced-motion.
const ProductLoadingFallback = () => (
  <div className="min-h-screen bg-[#FFF6EC] flex items-center justify-center" aria-busy="true">
    <NuggetState state="loading" role="status" />
  </div>
);

// Enhanced loading fallback with animation for lazy-loaded pages (not Home)
const LoadingFallback = IS_PRODUCT ? ProductLoadingFallback : () => (
  <div 
    className="min-h-screen bg-gray-50 flex items-center justify-center" 
    role="status" 
    aria-busy="true"
    aria-label={IS_PRODUCT ? `Loading ${BRAND.name}` : "Loading Utopian Creations website"}
  >
    <InlineLoadingAnimation 
      messages={[
        `Loading ${BRAND.name}...`,
        "Preparing your experience...",
        "Almost there..."
      ]} 
    />
    <span className="sr-only">Content is loading. Please wait.</span>
  </div>
);

// ---------------------------------------------------------------------------
// Product mode (VITE_BRAND=hollerpost → app.hollerpost.com). The route table is
// deliberately tiny: auth, the dashboard, and the legal pages. None of the
// agency pages (Home, services, intake, blueprint, booking, admin…) are
// reachable, and scripts/prerender-shells.mjs emits a matching _redirects so
// every other path is a real HTTP 404 at the edge. If you add a route here,
// add it to PRODUCT_ROUTES in that script too (dashboard sections come from
// src/lib/dashboardSections.js, which both read).
// ---------------------------------------------------------------------------

// `/`: signed in → dashboard, otherwise → signup. Query params (plan, ref,
// utm_*) ride along so ad/referral links to the bare origin keep attribution.
const ProductRoot = () => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingFallback />;
  return <Navigate to={`${currentUser ? '/dashboard' : '/signup'}${location.search}`} replace />;
};

// Hollerpost's legal pages live on the marketing site (hollerpost.com), not in
// this app — the edge already 301s /terms-of-service and /privacy-policy
// there (scripts/prerender-shells.mjs PRODUCT_REDIRECTS), but anyone who gets
// here via client-side navigation (a <Link>, browser back/forward, a cached
// SPA shell) needs the same redirect done in-app. window.location.replace
// (not <Navigate>) because the destination is a different origin entirely.
const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return <LoadingFallback />;
};

// One page_view per dashboard view change (docs/PRODUCT_ANALYTICS.md).
const ProductPageTracker = () => {
  const location = useLocation();
  useEffect(() => { trackPage(location.pathname, location.search); }, [location.pathname, location.search]);
  return null;
};

const ProductRoutes = () => (
  <Routes>
    <Route path="/" element={<ProductRoot />} />
    {EditorLab && <Route path="/editor-lab" element={<EditorLab />} />}
    <Route path="/login" element={<AuthPage mode="login" />} />
    <Route path="/signup" element={<AuthPage mode="signup" />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/dashboard/:section" element={<Dashboard />} />
    <Route path="/partner" element={<PartnerDashboard />} />
    <Route path="/settings" element={<Navigate to="/dashboard/settings" replace />} />
    <Route path="/terms-of-service" element={<ExternalRedirect to="https://hollerpost.com/terms/" />} />
    <Route path="/privacy-policy" element={<ExternalRedirect to="https://hollerpost.com/privacy/" />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

function App() {
  // Microsoft Ads UET tag — no-op unless VITE_BING_UET_ID is configured.
  useEffect(() => { initUet(); }, []);
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Router>
          <ScrollToTop />
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingFallback />}>
              {IS_PRODUCT ? <><ProductPageTracker /><ProductRoutes /></> : (
              <Routes>
              <Route path="/" element={<Home />} />
              {EditorLab && <Route path="/editor-lab" element={<EditorLab />} />}
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/quote" element={<Quote />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* Referenced by the toll-free program's sample message body —
                  must render real content, not a blank SPA fallthrough. */}
              <Route path="/specials" element={<Specials />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:id" element={<InsightPost />} />
              <Route path="/lezfindr-showcase" element={<LezFindrShowcase />} />

              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<ChromeExtensionPrivacy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/thanks" element={<Thanks />} />
              <Route path="/admin" element={<AdminAuth />} />
              <Route path="/admin-hub" element={<AdminRoute><AdminHub /></AdminRoute>} />
              <Route path="/admin-panel" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin-projects" element={<AdminRoute><AdminProjects /></AdminRoute>} />
              <Route path="/admin-contacts" element={<AdminRoute><AdminContacts /></AdminRoute>} />
              <Route path="/admin-blog" element={<AdminRoute><BlogManagement /></AdminRoute>} />
              <Route path="/free-website-form" element={<FreeWebsiteForm />} />
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
              <Route path="/auth/google/callback" element={<GoogleOAuthCallback />} />
              <Route path="/client-portal" element={<Navigate to="/dashboard" replace />} />
              <Route path="/client-portal/new-project" element={<AuthenticatedQuoteForm />} />
              <Route path="/book" element={<Booking />} />
              <Route path="/booking/manage" element={<BookingManage />} />
              <Route path="/simple-tech-help" element={<SimpleTechHelp />} />
              <Route path="/docs/email-api" element={<EmailApiDocs />} />
              <Route path="/start" element={<Start />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/signup" element={<AuthPage mode="signup" />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/intake" element={<Intake />} />
              <Route path="/intake/github-callback" element={<GithubCallback />} />
              <Route path="/blueprint/:leadId" element={<Blueprint />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/:section" element={<Dashboard />} />
              <Route path="/brainstorm/:repoId" element={<BrainstormSession />} />

              {/* Service Pages */}
              <Route path="/services/custom-web-development" element={<CustomWebDevelopment />} />
              <Route path="/services/mobile-app-development" element={<MobileAppDevelopment />} />
              <Route path="/services/ai-integration" element={<AIIntegration />} />
              <Route path="/services/ai-to-production" element={<Navigate to="/services/ai-integration" replace />} />
              <Route path="/services/email-sms-marketing" element={<EmailMarketingSaaS />} />
              {/* Ads-only landing page (noindex) — Microsoft Ads traffic */}
              <Route path="/go/email-marketing" element={<EmailMarketingLP />} />
              <Route path="/go/website" element={<WebsiteLP />} />
              <Route path="/services/technology-consulting" element={<TechnologyConsulting />} />
              <Route path="/services/support-maintenance" element={<Navigate to="/services/technology-consulting" replace />} />

              {/* Portfolio Pages */}
              <Route path="/portfolio/moffatt-ranch" element={<MoffattRanch />} />
              <Route path="/portfolio/ravenscroft-design" element={<RavenscroftDesign />} />
              <Route path="/portfolio/neesh-art" element={<NeeshArt />} />
              <Route path="/portfolio/mirada" element={<Mirada />} />

              {/* Catch-all. Without this an unmatched path rendered nothing at
                  all — a blank white page instead of a usable 404. */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              )}
            </Suspense>
          </AnimatePresence>
            {/* FloatingChat: route-aware lazy wrapper — only loads on non-admin pages.
                Agency-only: product mode mounts no chat widget at all. */}
            {!IS_PRODUCT && (
              <Suspense fallback={null}>
                <FloatingChatRoute />
              </Suspense>
            )}
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
